import React, { useMemo } from 'react';
import { Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@apollo/client/react';
import { GET_COURSE_QUIZZES_WITH_ANSWERS } from 'helper/graphql/queries';

// Extracts flat question list from survey-core questionData JSON
const parseQuestions = (questionData) => {
	if (!questionData) return [];
	try {
		const data = typeof questionData === 'string' ? JSON.parse(questionData) : questionData;
		const questions = [];
		(data?.pages || []).forEach((page) => {
			(page?.elements || []).forEach((el) => questions.push(el));
		});
		return questions;
	} catch {
		return [];
	}
};

// null = no correctAnswer defined, true/false = graded result
const checkCorrect = (question, userAnswer) => {
	if (question.correctAnswer === undefined || question.correctAnswer === null) return null;
	const correct = question.correctAnswer;
	if (Array.isArray(correct)) {
		const userArr = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
		return correct.length === userArr.length && correct.every((v) => userArr.includes(v));
	}
	return String(userAnswer) === String(correct);
};

const formatValue = (val) => {
	if (val === undefined || val === null || val === '') return null;
	if (Array.isArray(val)) return val.join(', ');
	return String(val);
};

const QuestionResult = ({ question, answerData }) => {
	const userAnswer = answerData?.[question.name];
	const result = checkCorrect(question, userAnswer);
	const displayAnswer = formatValue(userAnswer);

	return (
		<div className={`qr-question-block mb-2 ${result === true ? 'qr-q-correct' : result === false ? 'qr-q-wrong' : ''}`}>
			<div className="d-flex align-items-start justify-content-between gap-2 mb-2">
				<p className="qr-question-title mb-0">{question.title || question.name}</p>
				{result === true && (
					<Badge bg="" className="qr-badge qr-badge-correct">
						<i className="fe fe-check me-1"></i>Correct
					</Badge>
				)}
				{result === false && (
					<Badge bg="" className="qr-badge qr-badge-wrong">
						<i className="fe fe-x me-1"></i>Wrong
					</Badge>
				)}
				{result === null && displayAnswer && (
					<Badge bg="" className="qr-badge qr-badge-answered">
						<i className="fe fe-edit-2 me-1"></i>Answered
					</Badge>
				)}
			</div>

			<div className="qr-answer-row">
				<span className="qr-answer-label">Answer:</span>
				<span className="qr-answer-value">
					{displayAnswer ?? <span className="text-muted fst-italic">No answer</span>}
				</span>
			</div>

			{result === false && (
				<div className="qr-answer-row qr-correct-answer-row mt-1">
					<span className="qr-answer-label">Correct:</span>
					<span className="qr-answer-value qr-correct-text">
						{Array.isArray(question.correctAnswer)
							? question.correctAnswer.join(', ')
							: String(question.correctAnswer)}
					</span>
				</div>
			)}
		</div>
	);
};

const QuizResponsesModal = ({ show, onHide, user, courseGuid }) => {
	const { data, loading } = useQuery(GET_COURSE_QUIZZES_WITH_ANSWERS, {
		variables: { guid: courseGuid },
		skip: !courseGuid || !show,
	});

	// Collect every quiz in the course that this user has answered
	const userQuizResults = useMemo(() => {
		if (!data || !user?.guid) return [];

		const modules = data?.allCourses?.edges[0]?.node?.coursemodulesSet?.edges || [];
		const results = [];

		modules.forEach((modEdge) => {
			const quizzes = modEdge.node?.modulecontentquizSet?.edges || [];
			quizzes.forEach((quizEdge) => {
				const quiz = quizEdge.node;
				// Find this user's answer for the quiz
				const answerEdge = quiz.quizanswersSet?.edges?.find(
					(ae) => ae.node?.user?.guid === user.guid
				);
				if (!answerEdge) return; // user hasn't answered this quiz

				let parsedAnswerData = {};
				try {
					parsedAnswerData =
						typeof answerEdge.node.answerData === 'string'
							? JSON.parse(answerEdge.node.answerData)
							: answerEdge.node.answerData || {};
				} catch {
					parsedAnswerData = {};
				}

				results.push({
					quizGuid: quiz.guid,
					quizTitle: quiz.title,
					moduleTitle: modEdge.node.title,
					questions: parseQuestions(quiz.questionData),
					answerData: parsedAnswerData,
				});
			});
		});

		return results;
	}, [data, user]);

	const userName = user ? `${user.firstName} ${user.lastName}` : '';

	return (
		<Modal show={show} onHide={onHide} size="lg" centered scrollable>
			<Modal.Header closeButton className="qr-modal-header">
				<div>
					<Modal.Title className="qr-modal-title">
						<i className="fe fe-clipboard me-2"></i>Quiz Responses
					</Modal.Title>
					{userName && <p className="qr-modal-subtitle mb-0">{userName}</p>}
				</div>
			</Modal.Header>

			<Modal.Body className="qr-modal-body">
				{loading && (
					<div className="text-center py-5">
						<Spinner animation="border" style={{ color: '#F68521' }} />
						<p className="mt-3 text-muted">Loading responses...</p>
					</div>
				)}

				{!loading && userQuizResults.length === 0 && (
					<Alert variant="secondary" className="qr-empty-state text-center py-4">
						<i className="fe fe-inbox" style={{ fontSize: '2rem', opacity: 0.4 }}></i>
						<p className="mt-2 mb-0">
							This student has not submitted any quiz responses for this course.
						</p>
					</Alert>
				)}

				{!loading &&
					userQuizResults.map((result) => {
						const correctCount = result.questions.filter(
							(q) => checkCorrect(q, result.answerData[q.name]) === true
						).length;
						const gradedCount = result.questions.filter(
							(q) => checkCorrect(q, result.answerData[q.name]) !== null
						).length;

						return (
							<div key={result.quizGuid} className="qr-quiz-block mb-4">
								<div className="qr-quiz-header d-flex align-items-center justify-content-between mb-3">
									<div>
										<p className="qr-module-label mb-1">
											<i className="fe fe-layers me-1"></i>
											{result.moduleTitle}
										</p>
										<h6 className="qr-quiz-title mb-0">
											<i className="fe fe-help-circle me-2 text-muted"></i>
											{result.quizTitle || 'Untitled Quiz'}
										</h6>
									</div>
									{gradedCount > 0 && (
										<Badge bg="" className="qr-score-badge">
											{correctCount}/{gradedCount} correct
										</Badge>
									)}
								</div>

								{result.questions.length === 0 && (
									<p className="text-muted fst-italic" style={{ fontSize: '0.875rem' }}>
										No questions found for this quiz.
									</p>
								)}

								{result.questions.map((question, idx) => (
									<QuestionResult
										key={question.name || idx}
										question={question}
										answerData={result.answerData}
									/>
								))}
							</div>
						);
					})}
			</Modal.Body>

			<style>{`
				.qr-modal-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					border-bottom: 2px solid #e2e8f0;
					padding: 1.25rem 1.5rem;
				}
				.qr-modal-title {
					font-size: 1.125rem;
					font-weight: 700;
					color: #1e293b;
					display: flex;
					align-items: center;
				}
				.qr-modal-subtitle {
					font-size: 0.875rem;
					color: #64748b;
					margin-top: 0.25rem;
				}
				.qr-modal-body {
					padding: 1.5rem;
					background: #f8fafc;
				}
				.qr-quiz-block {
					background: white;
					border-radius: 12px;
					padding: 1.25rem;
					box-shadow: 0 2px 8px rgba(0,0,0,0.06);
					border: 1px solid #e2e8f0;
				}
				.qr-quiz-header {
					border-bottom: 1px solid #f1f5f9;
					padding-bottom: 0.75rem;
				}
				.qr-module-label {
					font-size: 0.75rem;
					color: #94a3b8;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.4px;
				}
				.qr-quiz-title {
					font-size: 0.9375rem;
					font-weight: 700;
					color: #1e293b;
				}
				.qr-score-badge {
					background: rgba(246, 133, 33, 0.1) !important;
					color: #F68521 !important;
					font-size: 0.8125rem;
					font-weight: 700;
					padding: 5px 12px;
					border-radius: 8px;
				}
				.qr-question-block {
					background: #f8fafc;
					border-radius: 8px;
					padding: 0.875rem 1rem;
					border-left: 3px solid #e2e8f0;
				}
				.qr-q-correct { border-left-color: #10b981; }
				.qr-q-wrong   { border-left-color: #ef4444; }
				.qr-question-title {
					font-size: 0.875rem;
					font-weight: 600;
					color: #334155;
					line-height: 1.5;
					flex: 1;
				}
				.qr-badge {
					font-size: 0.6875rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 6px;
					white-space: nowrap;
					flex-shrink: 0;
					display: inline-flex;
					align-items: center;
				}
				.qr-badge-correct {
					background: rgba(16, 185, 129, 0.12) !important;
					color: #10b981 !important;
				}
				.qr-badge-wrong {
					background: rgba(239, 68, 68, 0.12) !important;
					color: #ef4444 !important;
				}
				.qr-badge-answered {
					background: rgba(29, 158, 176, 0.1) !important;
					color: #1d9eb0 !important;
				}
				.qr-answer-row {
					display: flex;
					align-items: baseline;
					gap: 0.5rem;
					font-size: 0.8125rem;
				}
				.qr-answer-label {
					color: #94a3b8;
					font-weight: 600;
					text-transform: uppercase;
					font-size: 0.6875rem;
					letter-spacing: 0.4px;
					flex-shrink: 0;
				}
				.qr-answer-value {
					color: #475569;
					font-weight: 500;
				}
				.qr-correct-answer-row {
					border-top: 1px dashed #e2e8f0;
					padding-top: 0.5rem;
				}
				.qr-correct-text {
					color: #10b981 !important;
					font-weight: 600;
				}
				.qr-empty-state {
					background: white;
					border: 2px dashed #e2e8f0;
					color: #94a3b8;
					border-radius: 12px;
				}
			`}</style>
		</Modal>
	);
};

export default QuizResponsesModal;
